export const FIXED_PROVIDER_FIELDS = ["id", "name", "is_active", "created_at", "updated_at"];

export const ENV_REFERENCE_HINT =
  "Use an environment reference such as $env:SARVAM_API_KEY";

const FIXED = new Set(FIXED_PROVIDER_FIELDS);
const RESERVED_CONFIG = new Set(["id", "created_at", "updated_at"]);
const SECRET_KEY = /(?:^|[_-])(?:api[_-]key|token|password|subscription[_-]key)$/i;
const ENV_REFERENCE = /^\$env:[A-Za-z_][A-Za-z0-9_]*$/;

let rowSequence = 0;

export function createConfigRow(partial = {}) {
  rowSequence += 1;
  return {
    id: partial.id || `config-${rowSequence}`,
    key: partial.key ?? "",
    type: partial.type || "text",
    raw: partial.raw ?? "",
    originalKey: partial.originalKey ?? null,
    originalValue: Object.prototype.hasOwnProperty.call(partial, "originalValue")
      ? partial.originalValue
      : undefined,
  };
}

export function isSecretKey(key) {
  return SECRET_KEY.test(String(key ?? "").trim());
}

export function isEnvReference(value) {
  return typeof value === "string" && ENV_REFERENCE.test(value.trim());
}

export function isRawSecret(value) {
  if (typeof value !== "string") return false;
  const text = value.trim();
  return text.startsWith("sk-") || text.startsWith("sk_") || /^bearer\s/i.test(text);
}

function containsRawSecret(value) {
  if (typeof value === "string") return isRawSecret(value);
  if (Array.isArray(value)) return value.some(containsRawSecret);
  if (value && typeof value === "object") {
    return Object.values(value).some(containsRawSecret);
  }
  return false;
}

export function inferConfigField(value) {
  if (typeof value === "boolean") {
    return { type: "boolean", raw: value ? "true" : "false" };
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return { type: "number", raw: String(value) };
  }
  if (value !== null && typeof value === "object") {
    return { type: "json", raw: JSON.stringify(value, null, 2) };
  }
  return { type: "text", raw: value == null ? "" : String(value) };
}

export function configEntries(provider) {
  if (!provider || typeof provider !== "object" || Array.isArray(provider)) return [];
  return Object.entries(provider).filter(([key]) => !FIXED.has(key));
}

export function rowsFromProvider(provider) {
  return configEntries(provider).map(([key, value]) => {
    const field = inferConfigField(value);
    return createConfigRow({
      key,
      type: field.type,
      raw: field.raw,
      originalKey: key,
      originalValue: value,
    });
  });
}

export function configJson(provider) {
  return JSON.stringify(Object.fromEntries(configEntries(provider)), null, 2);
}

function stableStringify(value) {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function valuesEqual(left, right) {
  return stableStringify(left) === stableStringify(right);
}

function isRowBlank(row) {
  return !String(row.key ?? "").trim() && !String(row.raw ?? "").trim();
}

export function parseConfigValue(type, raw) {
  if (type === "number") {
    const trimmed = String(raw ?? "").trim();
    if (trimmed === "" || Number.isNaN(Number(trimmed))) {
      return { error: "Enter a number" };
    }
    return { value: Number(trimmed) };
  }

  if (type === "boolean") {
    if (raw === true || raw === "true") return { value: true };
    if (raw === false || raw === "false") return { value: false };
    return { error: "Choose true or false" };
  }

  if (type === "json") {
    try {
      const parsed = JSON.parse(String(raw ?? ""));
      if (parsed === null || typeof parsed !== "object") {
        return { error: "Enter a JSON array or object" };
      }
      return { value: parsed };
    } catch {
      return { error: "Enter valid JSON" };
    }
  }

  return { value: raw == null ? "" : String(raw) };
}

function parseRow(row) {
  const key = String(row.key ?? "").trim();
  if (!key) {
    if (isRowBlank(row)) return { skip: true };
    return { error: "Key is required" };
  }
  if (RESERVED_CONFIG.has(key) || key === "name" || key === "is_active") {
    return { error: "This key is reserved" };
  }

  const parsed = parseConfigValue(row.type, row.raw);
  if (parsed.error) return { error: parsed.error };

  if (isSecretKey(key) && !isEnvReference(parsed.value)) {
    return { error: ENV_REFERENCE_HINT };
  }
  if (containsRawSecret(parsed.value)) {
    return { error: `Raw secrets are not allowed. ${ENV_REFERENCE_HINT}` };
  }

  return { key, value: parsed.value };
}

export function configRowError(row, rows = []) {
  const result = parseRow(row);
  if (result.skip) return "Key is required";
  if (result.error) return result.error;
  const duplicate = rows.some(
    (item) => item.id !== row.id && String(item.key ?? "").trim() === result.key,
  );
  return duplicate ? "Duplicate key" : "";
}

function validateRows(rows) {
  const errors = {};
  const parsed = [];
  const seen = new Map();

  rows.forEach((row) => {
    const result = parseRow(row);
    if (result.skip) return;
    if (result.error) {
      errors[row.id] = result.error;
      return;
    }
    if (seen.has(result.key)) {
      errors[row.id] = "Duplicate key";
      errors[seen.get(result.key)] = "Duplicate key";
      return;
    }
    seen.set(result.key, row.id);
    parsed.push({ id: row.id, key: result.key, value: result.value });
  });

  return { errors, parsed };
}

function nameError(name) {
  return String(name ?? "").trim() ? "" : "Name is required";
}

export function buildVoiceProviderCreate({ name, isActive = true, rows = [] }) {
  const errors = { name: nameError(name), rows: {}, form: "" };
  const { errors: rowErrors, parsed } = validateRows(rows);
  errors.rows = rowErrors;

  if (errors.name || Object.keys(rowErrors).length > 0) {
    return { ok: false, errors };
  }

  const payload = {
    name: String(name).trim(),
    is_active: Boolean(isActive),
  };
  parsed.forEach((row) => {
    payload[row.key] = row.value;
  });

  return { ok: true, payload, errors };
}

export function buildVoiceProviderPatch({
  provider,
  name,
  isActive = true,
  rows = [],
}) {
  const errors = { name: nameError(name), rows: {}, form: "" };
  const { errors: rowErrors, parsed } = validateRows(rows);
  errors.rows = rowErrors;

  if (errors.name || Object.keys(rowErrors).length > 0) {
    return { ok: false, errors };
  }

  const original = Object.fromEntries(configEntries(provider));
  const current = new Map(parsed.map((row) => [row.key, row.value]));
  const payload = {};
  const nextName = String(name).trim();

  if (nextName !== String(provider?.name ?? "")) payload.name = nextName;
  if (Boolean(isActive) !== Boolean(provider?.is_active)) {
    payload.is_active = Boolean(isActive);
  }

  current.forEach((value, key) => {
    if (!Object.prototype.hasOwnProperty.call(original, key) || !valuesEqual(original[key], value)) {
      payload[key] = value;
    }
  });

  Object.keys(original).forEach((key) => {
    if (!current.has(key)) payload[key] = null;
  });

  delete payload.id;
  delete payload.created_at;
  delete payload.updated_at;

  if (Object.keys(payload).length === 0) {
    return {
      ok: false,
      errors: { ...errors, form: "No changes to save" },
    };
  }

  return { ok: true, payload, errors };
}

function detailMessage(detail) {
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === "string" ? item : item?.msg))
      .filter(Boolean)
      .join("; ");
  }
  return "";
}

export function mapVoiceProviderError(error) {
  const status = error?.status;
  const data = error?.data;
  const detail = data?.detail ?? data?.message ?? data;
  const result = { name: "", isActive: "", form: "", fields: {} };

  if (status === 409) {
    result.name = detailMessage(detail) || "Name already exists";
    return result;
  }

  if (status === 422 && Array.isArray(detail)) {
    detail.forEach((item) => {
      if (!item || typeof item !== "object") {
        if (typeof item === "string") result.form = item;
        return;
      }
      const loc = Array.isArray(item.loc) ? item.loc.filter((part) => part !== "body") : [];
      const field = loc[loc.length - 1];
      const message = item.msg || "Invalid value";
      if (field === "name") result.name = message;
      else if (field === "is_active") result.isActive = message;
      else if (typeof field === "string") result.fields[field] = message;
      else result.form = message;
    });
    if (!result.name && !result.isActive && !result.form && Object.keys(result.fields).length === 0) {
      result.form = detailMessage(detail) || "Check the form and try again";
    }
    return result;
  }

  if (status === 422 && detail && typeof detail === "object" && !Array.isArray(detail)) {
    Object.entries(detail).forEach(([key, value]) => {
      const message = Array.isArray(value) ? value.join(", ") : String(value);
      if (key === "name") result.name = message;
      else if (key === "is_active") result.isActive = message;
      else result.fields[key] = message;
    });
    return result;
  }

  const message = detailMessage(detail) || (typeof error === "string" ? error : "");
  if (status === 422 && /name/i.test(message) && !/secret|api[_-]?key|token|password/i.test(message)) {
    result.name = message;
    return result;
  }
  result.form = message || "Something went wrong";
  return result;
}

export function voiceProviderErrorMessage(error, fallback) {
  if (typeof error === "string" && error.trim()) return error;
  const mapped = mapVoiceProviderError(error);
  return mapped.name || mapped.isActive || mapped.form || fallback;
}
